import React from 'react';
import styled from '@emotion/styled';
import type { StayCalendar } from '../../../../interfaces/stays';
import { DayNumber } from '../../../shared/month-grid/month-grid';
import type { DayContext } from '../../../shared/month-grid/month-grid';
import { StatusPill } from '../../../status-pill/status-pill';
import { VisuallyHidden } from '../../../../style-utils/text';
import { duration, easing, palette, radius, spacing, status, typography } from '../../../../style-utils/theme';
import {
  CalendarIndex,
  DayRoles,
  PROVIDER_TEXT,
  blockText,
  departureText,
  firstNightText,
  footerOf,
  gapText,
  laterNightText,
} from './calendar-model';

/**
 * ONE DAY OF THE OPERATIONS CALENDAR — the body MonthGrid draws inside the
 * day's own button.
 *
 * ≥ md: the number, "Today", the pills and the departure-day footer.
 * < md: the number and up to three marks (stay bar, block hatch, gap dot); every
 * word lives in the day button's accessible name (dayLabelOf) and in the Day
 * sheet a tap opens.
 *
 * The day IS a native button, so a pill cannot be another one (a button inside
 * a button is invalid and screen readers flatten it anyway). A pill is a span
 * that takes the CLICK (with stays:manage) and stops it there, so a click on a
 * stay opens it and a click beside it selects the day. The keyboard reaches the
 * same stays through the Day sheet (Space on a day).
 */

/* The two block hatches: stripes of palette tokens, never a raw colour. */
export const OWNER_HATCH = `repeating-linear-gradient(135deg, ${palette.surfaceMuted} 0, ${palette.surfaceMuted} 4px, ${palette.lineStrong} 4px, ${palette.lineStrong} 8px)`;
export const PROVIDER_HATCH = `repeating-linear-gradient(45deg, ${palette.tealTintSoft} 0, ${palette.tealTintSoft} 4px, ${palette.line} 4px, ${palette.line} 8px)`;

const Body = styled.span`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing.half};
  width: 100%;
  min-width: 0;
  text-align: left;
`;

const Top = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing.half};
  min-width: 0;
`;

const TodayWord = styled.span`
  min-width: 0;
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.lagoon};
  overflow-wrap: anywhere;
`;

export type PillKind = 'stay' | 'arrival' | 'departure' | 'block' | 'provider';

const pillBg = ({ $kind }: { $kind: PillKind }): string => {
  switch ($kind) {
    case 'stay':
    case 'arrival':
      return status.brand.bg;
    case 'departure':
      return status.neutral.bg;
    case 'block':
      return OWNER_HATCH;
    default:
      return PROVIDER_HATCH;
  }
};

const pillFg = ({ $kind }: { $kind: PillKind }): string => {
  switch ($kind) {
    case 'stay':
    case 'arrival':
      return status.brand.fg;
    case 'departure':
      return status.neutral.fg;
    default:
      return palette.ink;
  }
};

export const Pill = styled.span<{ $kind: PillKind; $interactive?: boolean }>`
  position: relative;
  display: block;
  min-width: 0;
  padding: ${spacing.half} ${spacing.half} ${spacing.half} ${({ $kind }) => ($kind === 'arrival' ? spacing.xs : spacing.half)};
  border-radius: ${radius.xs};
  background: ${pillBg};
  color: ${pillFg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.medium};
  line-height: ${typography.leading.snug};
  text-align: left;
  overflow-wrap: anywhere;
  white-space: normal;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'inherit')};
  transition: background-color ${duration.fast} ${easing.standard}, color ${duration.fast} ${easing.standard};

  &::before {
    content: '';
    display: ${({ $kind }) => ($kind === 'arrival' ? 'block' : 'none')};
    position: absolute;
    top: ${spacing.half};
    bottom: ${spacing.half};
    left: ${spacing.half};
    width: 2px;
    border-radius: 2px;
    background: ${palette.teal};
  }

  &:hover {
    color: ${({ $interactive, $kind }) => ($interactive ? palette.ink : pillFg({ $kind }))};
  }
`;

const RevokedDot = styled.span`
  display: inline-block;
  width: ${spacing.xs};
  height: ${spacing.xs};
  margin-left: ${spacing.half};
  border-radius: ${radius.circle};
  background: ${palette.alert};
  vertical-align: middle;
`;

const Footer = styled.span<{ $urgent: boolean }>`
  display: block;
  min-width: 0;
  padding: ${spacing.half};
  border-radius: ${radius.xs};
  background: ${({ $urgent }) => ($urgent ? status.warning.bg : 'transparent')};
  color: ${({ $urgent }) => ($urgent ? status.warning.fg : palette.slate)};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.medium};
  line-height: ${typography.leading.snug};
  text-align: left;
  overflow-wrap: anywhere;
`;

const GapPill = styled(StatusPill)`
  white-space: normal;
  overflow-wrap: anywhere;
`;

/* ── the phone's marks ── */

const Marks = styled.span`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing.half};
  width: 100%;
  min-width: 0;
`;

const Bar = styled.span<{ $first?: boolean }>`
  display: block;
  height: ${spacing.half};
  margin-left: ${({ $first }) => ($first ? '50%' : '0')};
  border-radius: 2px;
  background: ${palette.teal};
`;

const Hatch = styled.span<{ $provider?: boolean }>`
  display: block;
  height: ${spacing.xs};
  border-radius: 2px;
  background: ${({ $provider }) => ($provider ? PROVIDER_HATCH : OWNER_HATCH)};
`;

const Dot = styled.span<{ $warn: boolean }>`
  display: block;
  align-self: center;
  width: ${spacing.xs};
  height: ${spacing.xs};
  border-radius: ${radius.circle};
  background: ${({ $warn }) => ($warn ? palette.warning : palette.slate)};
`;

export interface CalendarDayProps {
  day: string;
  ctx: DayContext;
  roles: DayRoles;
  index: CalendarIndex;
  data: StayCalendar;
  compact: boolean;
  canManage: boolean;
  onOpenStay: (id: string) => void;
}

const stopPress = (event: React.PointerEvent) => event.stopPropagation();

export const CalendarDay: React.FC<CalendarDayProps> = ({ day, ctx, roles, index, data, compact, canManage, onOpenStay }) => {
  if (compact) {
    const hasMarks = !!(roles.night || roles.block || roles.provider || roles.gap);

    return (
      <Body>
        <DayNumber day={day} />
        {hasMarks ? (
          <Marks aria-hidden="true" data-marks="">
            {roles.night ? <Bar $first={roles.night.first} data-mark="stay" /> : null}
            {roles.block || roles.provider ? <Hatch $provider={!roles.block} data-mark="block" /> : null}
            {roles.gap ? <Dot $warn={roles.gap.nights === 1} data-mark="gap" /> : null}
          </Marks>
        ) : null}
      </Body>
    );
  }

  const open = (id: string) => (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    onOpenStay(id);
  };
  const interactive = canManage;
  const footer = footerOf(roles, index, data);
  const night = roles.night;
  const departure = roles.departure;

  return (
    <Body>
      <Top>
        <DayNumber day={day} />
        {ctx.isToday ? <TodayWord aria-hidden="true">Today</TodayWord> : null}
      </Top>
      {departure ? (
        <Pill
          $kind="departure"
          $interactive={interactive}
          data-pill="departure"
          data-stay-id={departure.id}
          onClick={interactive ? open(departure.id) : undefined}
          onPointerDown={interactive ? stopPress : undefined}
        >
          {departureText(departure)}
          {departure.access_revoked ? (
            <>
              <RevokedDot aria-hidden="true" />
              <VisuallyHidden> (guest access revoked)</VisuallyHidden>
            </>
          ) : null}
        </Pill>
      ) : null}
      {night ? (
        <Pill
          $kind={night.first ? 'arrival' : 'stay'}
          $interactive={interactive}
          data-pill={night.first ? 'arrival' : 'stay'}
          data-stay-id={night.stay.id}
          onClick={interactive ? open(night.stay.id) : undefined}
          onPointerDown={interactive ? stopPress : undefined}
        >
          {night.first ? firstNightText(night.stay) : laterNightText(night)}
          {night.stay.access_revoked ? (
            <>
              <RevokedDot aria-hidden="true" />
              <VisuallyHidden> (guest access revoked)</VisuallyHidden>
            </>
          ) : null}
        </Pill>
      ) : null}
      {roles.block ? (
        <Pill $kind="block" data-pill="block" data-block-id={roles.block.id}>
          {blockText(roles.block)}
        </Pill>
      ) : null}
      {roles.provider ? (
        <Pill $kind="provider" data-pill="provider" data-block-id={roles.provider.id}>
          {PROVIDER_TEXT}
        </Pill>
      ) : null}
      {roles.gap ? (
        <GapPill tone={roles.gap.nights === 1 ? 'warning' : 'neutral'} data-gap={roles.gap.nights}>
          {gapText(roles.gap.nights)}
        </GapPill>
      ) : null}
      {footer ? (
        <Footer $urgent={footer.urgent} data-footer={footer.kind} data-urgent={footer.urgent ? 'true' : undefined}>
          {footer.text}
        </Footer>
      ) : null}
    </Body>
  );
};
